import React, { useEffect, useMemo, useState } from 'react';
import { AddManageClientsPanel, AddManageCompanyPanel, AddManageSitesPanel } from './SuperAdminPanel';
import { AddManageInternalUsersPanel } from './AddManageInternalUsersPanel';
import { SubmittedFormsPanel } from './SubmittedFormsPanel';
import { HazardsAndControlPanel } from './HazardsAndControlPanel';
import { SUBMITTED_AND_HAZARDS_SECTION, SUBMITTED_FORMS_VIEW, HAZARDS_CONTROL_VIEW } from '../config/dashboardViews';
import { SafetyFormPanel } from './SafetyFormPanel';
import {
  CONFIGURE_PROFILE_VIEW,
  CollapsibleAccountInfo,
  ConfigureProfilePanel,
  SidebarUserHeading,
} from './ConfigureProfilePanel';
import { LogoutButton, adminActionButtonClass } from './LogoutButton';
import { MobileActionBar, useIsDesktop } from './MobileActionBar';
import { DashboardCardSection } from './DashboardCardSection';
import { ScrollableRightPane } from './ScrollEdgeButtons';
import { CollapsibleSidebarNav } from './CollapsibleSidebarNav';
import { UserProfileProvider } from '../context/UserProfileContext';
import { CertificateNotificationsBell } from './CertificateNotificationsBell';
import { DEFAULT_SUPER_ADMIN_COMPANY } from '../config/defaults';
import { getSafetyFormCards, isSafetyFormView, parseSafetyFormIndex } from '../config/safetyForms';
import { isAboveSupervisorRole, isSuperAdmin, platformAdminRoleLabel } from '../utils/roles';

const INTERNAL_USERS_VIEW = 'add-manage-internal-users';
const sidebarSearchClass =
  'w-full mb-1.5 px-2.5 py-1.5 rounded-md bg-black border border-gray-700 text-xs text-gray-200 placeholder:text-gray-600 focus:outline-none focus:ring-1 focus:ring-amber-300/40 focus:border-amber-300/40';

function matchesFormSearch(card, query) {
  const q = String(query || '').trim().toLowerCase();
  if (!q) return true;
  const haystack = [card.title, card.description, card.frequencyNote]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return haystack.includes(q);
}

const ADMIN_SECTIONS = [
  {
    label: 'Clients, Companies & Sites/Projects',
    cards: [
      {
        id: 'add-manage-company',
        title: 'Add / Manage Company',
        description: 'Add or manage companies.',
        icon: '🏢',
      },
      {
        id: 'add-manage-clients',
        title: 'Add / Manage Company Employees',
        description: 'Add or manage Company Employees.',
        icon: '👥',
      },
      {
        id: 'add-manage-sites',
        title: 'Add / Manage Sites/Projects',
        description: 'Add or manage sites/projects.',
        icon: '📍',
      },
    ],
  },
  SUBMITTED_AND_HAZARDS_SECTION,
];

function AdminHeaderActions({
  activeView,
  internalUserCreateOpen,
  showInternalUsers,
  showNotificationsBell,
  onInternalUserCreateOpen,
  onNavigate,
  onOpenCertificateNotification,
  compact = false,
}) {
  const showInternalUserAdd =
    activeView === INTERNAL_USERS_VIEW && !internalUserCreateOpen;
  const showInternalUsersNav =
    showInternalUsers && activeView !== INTERNAL_USERS_VIEW && !isSafetyFormView(activeView);
  const showBell = showNotificationsBell && !isSafetyFormView(activeView);
  const compactClass = compact
    ? 'inline-flex h-9 items-center px-2.5 py-0 text-xs'
    : '';

  return (
    <>
      {showInternalUserAdd && (
        <button
          type="button"
          onClick={() => onInternalUserCreateOpen(true)}
          className={`${adminActionButtonClass} ${compactClass}`}
        >
          Add Internal User
        </button>
      )}
      {showBell ? (
        <CertificateNotificationsBell
          className="inline-flex items-center"
          buttonClassName={compact ? 'h-9 w-9' : ''}
          onOpenCertificate={onOpenCertificateNotification}
        />
      ) : null}
      {showInternalUsersNav && (
        <button
          type="button"
          onClick={() => onNavigate(INTERNAL_USERS_VIEW)}
          className={`${adminActionButtonClass} ${compactClass}`}
        >
          Internal Users
        </button>
      )}
      <LogoutButton className={compactClass} />
    </>
  );
}

function AdminHeaderToolbar(props) {
  const isDesktop = useIsDesktop();
  if (!isDesktop) return null;

  return (
    <div className="mb-2 flex items-center justify-end gap-2">
      <AdminHeaderActions {...props} />
    </div>
  );
}

function renderPanel(activeView, onBack, projectName, panelProps, showInternalUsers) {
  if (isSafetyFormView(activeView)) {
    const formIndex = parseSafetyFormIndex(activeView);
    return (
      <SafetyFormPanel formIndex={formIndex} projectName={projectName} onBack={onBack} />
    );
  }
  switch (activeView) {
    case 'add-manage-clients':
      return (
        <AddManageClientsPanel
          onBack={onBack}
          createOpen={panelProps.clientCreateOpen}
          onCreateOpenChange={panelProps.onClientCreateOpen}
          openCertificateEmployeeId={panelProps.openCertificateEmployeeId}
          openCertificateFocusKey={panelProps.openCertificateFocusKey}
          onCertificateFocusHandled={panelProps.onCertificateFocusHandled}
        />
      );
    case 'add-manage-company':
      return (
        <AddManageCompanyPanel
          onBack={onBack}
          createOpen={panelProps.companyCreateOpen}
          onCreateOpenChange={panelProps.onCompanyCreateOpen}
        />
      );
    case 'add-manage-sites':
      return (
        <AddManageSitesPanel
          onBack={onBack}
          createOpen={panelProps.siteCreateOpen}
          onCreateOpenChange={panelProps.onSiteCreateOpen}
        />
      );
    case INTERNAL_USERS_VIEW:
      return showInternalUsers ? (
        <AddManageInternalUsersPanel
          onBack={onBack}
          createOpen={panelProps.internalUserCreateOpen}
          onCreateOpenChange={panelProps.onInternalUserCreateOpen}
        />
      ) : null;
    case SUBMITTED_FORMS_VIEW:
      return <SubmittedFormsPanel onBack={onBack} />;
    case HAZARDS_CONTROL_VIEW:
      return <HazardsAndControlPanel onBack={onBack} />;
    default:
      return null;
  }
}

function findActiveCard(activeView, adminSections, formCards) {
  const adminCard = adminSections.flatMap((s) => s.cards).find((c) => c.id === activeView);
  if (adminCard) return adminCard;
  return formCards.find((c) => c.id === activeView);
}

export function SuperAdminDashboard({ user }) {
  const [activeView, setActiveView] = useState('home');
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [companyCreateOpen, setCompanyCreateOpen] = useState(false);
  const [clientCreateOpen, setClientCreateOpen] = useState(false);
  const [siteCreateOpen, setSiteCreateOpen] = useState(false);
  const [internalUserCreateOpen, setInternalUserCreateOpen] = useState(false);
  const [sidebarFormSearch, setSidebarFormSearch] = useState('');
  const [rightFormSearch, setRightFormSearch] = useState('');
  const [openCertificateEmployeeId, setOpenCertificateEmployeeId] = useState(null);
  const [openCertificateFocusKey, setOpenCertificateFocusKey] = useState(0);
  // Internal Users is Super Admin only — Super User must not see that button/panel.
  const showInternalUsers = isSuperAdmin(user?.role);
  const showNotificationsBell = isAboveSupervisorRole(user?.role);
  const dashboardTitle = showInternalUsers ? 'Super Admin Dashboard' : 'Super User Dashboard';
  const roleLabel = platformAdminRoleLabel(user?.role);
  const projectName = user?.project_name || DEFAULT_SUPER_ADMIN_COMPANY;
  const formCards = useMemo(() => getSafetyFormCards(projectName), [projectName]);
  const sidebarFormCards = useMemo(
    () => formCards.filter((card) => matchesFormSearch(card, sidebarFormSearch)),
    [formCards, sidebarFormSearch]
  );
  const homeFormCards = useMemo(
    () => formCards.filter((card) => matchesFormSearch(card, rightFormSearch)),
    [formCards, rightFormSearch]
  );
  const formSection = useMemo(
    () => ({ label: 'Safety Forms', cards: homeFormCards }),
    [homeFormCards]
  );
  const allSections = useMemo(
    () => [...ADMIN_SECTIONS, formSection],
    [formSection]
  );
  const allCards = useMemo(
    () => [...ADMIN_SECTIONS.flatMap((s) => s.cards), ...formCards],
    [formCards]
  );
  const activeCard = findActiveCard(activeView, ADMIN_SECTIONS, formCards);
  const isInternalUsersView = showInternalUsers && activeView === INTERNAL_USERS_VIEW;
  const isSubmittedFormsView = activeView === SUBMITTED_FORMS_VIEW;
  const isHazardsView = activeView === HAZARDS_CONTROL_VIEW;
  const isProfileView = activeView === CONFIGURE_PROFILE_VIEW;
  const isFormView = isSafetyFormView(activeView);
  const handleNavigate = (view) => {
    setActiveView(view);
    setMobileNavOpen(false);
  };

  useEffect(() => {
    if (!showInternalUsers && activeView === INTERNAL_USERS_VIEW) {
      setActiveView('home');
    }
  }, [showInternalUsers, activeView]);

  useEffect(() => {
    setCompanyCreateOpen(false);
    setClientCreateOpen(false);
    setSiteCreateOpen(false);
    setInternalUserCreateOpen(false);
  }, [activeView]);

  const panelProps = {
    companyCreateOpen,
    onCompanyCreateOpen: setCompanyCreateOpen,
    clientCreateOpen,
    onClientCreateOpen: setClientCreateOpen,
    siteCreateOpen,
    onSiteCreateOpen: setSiteCreateOpen,
    internalUserCreateOpen,
    onInternalUserCreateOpen: setInternalUserCreateOpen,
    openCertificateEmployeeId,
    openCertificateFocusKey,
    onCertificateFocusHandled: () => setOpenCertificateEmployeeId(null),
  };

  const headerProps = {
    activeView,
    companyCreateOpen,
    clientCreateOpen,
    siteCreateOpen,
    internalUserCreateOpen,
    showInternalUsers,
    showNotificationsBell,
    onCompanyCreateOpen: setCompanyCreateOpen,
    onClientCreateOpen: setClientCreateOpen,
    onSiteCreateOpen: setSiteCreateOpen,
    onInternalUserCreateOpen: setInternalUserCreateOpen,
    onNavigate: handleNavigate,
    onOpenCertificateNotification: (notification) => {
      setClientCreateOpen(false);
      setOpenCertificateEmployeeId(notification?.employee_id || null);
      setOpenCertificateFocusKey((key) => key + 1);
      handleNavigate('add-manage-clients');
    },
  };

  const showHeaderToolbar =
    activeView === 'home' ||
    isInternalUsersView ||
    isSubmittedFormsView ||
    isHazardsView ||
    isProfileView ||
    (activeView !== 'home' && activeCard && !isFormView);

  return (
    <UserProfileProvider email={user?.email}>
    <div className="relative h-screen bg-black text-white flex overflow-hidden">
      <MobileActionBar menuOpen={mobileNavOpen} onOpenMenu={() => setMobileNavOpen(true)}>
        {showHeaderToolbar ? <AdminHeaderActions {...headerProps} compact /> : null}
      </MobileActionBar>
      {mobileNavOpen ? (
        <button
          type="button"
          className="fixed inset-0 z-40 bg-black/70 lg:hidden"
          onClick={() => setMobileNavOpen(false)}
          aria-label="Close navigation"
        />
      ) : null}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-72 bg-gray-950 border-r border-gray-800 p-4 flex flex-col shrink-0 overflow-y-auto transition-transform duration-200 lg:static lg:z-auto lg:translate-x-0 lg:p-6 ${
          mobileNavOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <button
          type="button"
          onClick={() => setMobileNavOpen(false)}
          className="absolute right-3 top-3 inline-flex h-10 w-10 items-center justify-center rounded-md border border-gray-700 text-xl text-gray-300 hover:text-white lg:hidden"
          aria-label="Close navigation"
        >
          ×
        </button>
        <div className="mb-6">
          <SidebarUserHeading user={user} />
          {user ? (
            <CollapsibleAccountInfo
              email={user.email}
              roleLabel={roleLabel}
              roleClassName="text-amber-200/90"
              profileActive={isProfileView}
              onConfigureProfile={() => handleNavigate(CONFIGURE_PROFILE_VIEW)}
            />
          ) : null}
        </div>
        <button
          type="button"
          onClick={() => handleNavigate('home')}
          className={`w-full text-left px-4 py-2 rounded-md text-sm font-medium transition-colors mb-4 ${
            activeView === 'home'
              ? 'bg-gray-800 text-white'
              : 'text-gray-400 hover:bg-gray-900 hover:text-white'
          }`}
        >
          Dashboard
        </button>
        {allSections.map((section) => {
          const isSafetyForms = section.label === 'Safety Forms';
          if (isSafetyForms) {
            return (
              <CollapsibleSidebarNav
                key={section.label}
                label={section.label}
                cards={sidebarFormCards}
                activeView={activeView}
                onSelect={handleNavigate}
                searchValue={sidebarFormSearch}
                onSearchChange={setSidebarFormSearch}
                searchPlaceholder="Search forms…"
                searchInputClass={sidebarSearchClass}
                emptyText={
                  sidebarFormSearch.trim() ? 'No forms match your search.' : 'No forms.'
                }
              />
            );
          }
          return (
            <CollapsibleSidebarNav
              key={section.label}
              label={section.label}
              cards={section.cards}
              activeView={activeView}
              onSelect={handleNavigate}
            />
          );
        })}
      </aside>

      {isFormView ? (
        <main className="relative flex-1 min-w-0 min-h-0 flex flex-col overflow-hidden px-3 pb-3 pt-16 sm:px-4 lg:px-8 lg:pb-8 lg:pt-3">
          <div className="mx-auto w-full max-w-5xl flex flex-col flex-1 min-h-0">
            {showHeaderToolbar && <AdminHeaderToolbar {...headerProps} />}
            {activeView !== 'home' &&
            !isProfileView &&
            (activeCard || isInternalUsersView || isSubmittedFormsView || isHazardsView)
              ? renderPanel(activeView, () => setActiveView('home'), projectName, panelProps, showInternalUsers)
              : null}
          </div>
        </main>
      ) : (
        <ScrollableRightPane
          className="flex-1 min-w-0"
          scrollClassName="px-2 sm:px-4 pt-16 pb-3 lg:py-3"
        >
          <div className="mx-auto w-full max-w-none">
            {showHeaderToolbar && <AdminHeaderToolbar {...headerProps} />}

            {activeView === 'home' && (
              <>
                <h2 className="text-2xl font-semibold mb-2">{dashboardTitle}</h2>
                <p className="text-gray-400 text-xs mb-8">
                  {allCards.length} tasks — companies, users, sites, and safety forms.
                </p>
                {allSections.map((section) => (
                  <DashboardCardSection
                    key={section.label}
                    label={section.label}
                    cards={section.cards}
                    onOpenCard={setActiveView}
                    {...(section.label === 'Safety Forms'
                      ? {
                          searchValue: rightFormSearch,
                          onSearchChange: setRightFormSearch,
                        }
                      : {})}
                  />
                ))}
              </>
            )}

            {isProfileView && (
              <div className="border border-gray-800 rounded-lg bg-gray-950/70 p-4 md:p-5 min-h-full">
                <ConfigureProfilePanel onBack={() => setActiveView('home')} />
              </div>
            )}

            {activeView !== 'home' &&
              !isProfileView &&
              (activeCard || isInternalUsersView || isSubmittedFormsView || isHazardsView) && (
              <div className="border border-gray-800 rounded-lg bg-gray-950/70 p-2.5 md:p-3 min-h-full">
                {renderPanel(activeView, () => setActiveView('home'), projectName, panelProps, showInternalUsers)}
              </div>
            )}
          </div>
        </ScrollableRightPane>
      )}
    </div>
    </UserProfileProvider>
  );
}
